import { makeStyles } from "tss-react/mui";
import { INPUT_TYPES } from "@miplatform/constants";
import { Box } from "@mui/material";
import { MiSectionHeader } from "../MiSectionHeader/MiSectionHeader";
import { MiControlledInput } from "./MiControlledInput";
import { MiInput } from "./MiInput";

const applyBeforeOnChange = (item, value) => {
  return item.beforeOnChange ? item.beforeOnChange(value) : value;
};

const applyAfterOnChange = (item, value) => {
  if (item.afterOnChange) {
    item.afterOnChange(value);
  }
};

/**
 * @typedef {Object} DetailField
 * @property {string} name - The field name/identifier
 * @property {string} label - The field label
 * @property {any} [value] - The field value
 * @property {string} [fieldType] - The input type (e.g., INPUT_TYPES.CURRENCY, INPUT_TYPES.AUTOCOMPLETE)
 * @property {number} [padding] - Padding around the field
 * @property {number} [minWidth] - Minimum width of the field
 * @property {string|number} [width] - Width of the field
 * @property {number} [flexGrow] - Flex grow value
 * @property {Object} [style] - Additional inline styles
 * @property {string} [backgroundColor] - Background color of the field
 * @property {Object} [formControl] - Form control object for the field
 * @property {Function} [handleChange] - Custom change handler
 * @property {Function} [beforeOnChange] - Function to transform value before onChange
 * @property {Function} [afterOnChange] - Function to execute after onChange
 * @property {string|boolean} [error] - Error message for the field
 * @property {Array} [options] - Options for select/autocomplete fields
 *
 * Renders a set of detail fields with optional header and input controls.
 * @param {Object} props - The component props.
 * @param {DetailField[]} [props.detailFields=[]] - An array of field configuration objects.
 * @param {string} [props.containerStyle] - The CSS class name for the container.
 * @param {string|Object} [props.customWrapperStyle] - Custom styles or class name for the wrapper.
 * @param {Object} [props.headerProps] - Properties for the optional section header.
 * @param {Object} [props.control=null] - Form control object for controlled inputs.
 * @param {Object} [props.edit] - Object containing methods and data for managing field values and errors.
 * @param {Function} props.edit.update - Function to update field values.
 * @param {Function} props.edit.getValue - Function to retrieve the value of a field.
 * @param {Object} [props.edit.errors] - Object containing field-specific error messages.
 *
 * @returns {JSX.Element} The rendered component.
 */
export function MiDetailFields({
  detailFields = [],
  containerStyle,
  customWrapperStyle,
  headerProps,
  control = null,
  edit,
}) {
  const { classes, theme } = useStyles();

  const getInput = (item) => {
    const inputItem = { ...item };
    const formControl = inputItem.formControl || control;

    if (formControl) {
      return <MiControlledInput item={inputItem} formControl={formControl} />;
    }

    if (!inputItem.handleChange) {
      inputItem.handleChange = (e, data = null) => {
        let value = null;
        switch (inputItem.fieldType) {
          case INPUT_TYPES.CURRENCY:
          case INPUT_TYPES.AUTOCOMPLETE:
            value = data.value;
            break;
          case INPUT_TYPES.CHECKBOX:
          case INPUT_TYPES.SWITCH:
            value = e.target.checked;
            break;
          case INPUT_TYPES.NUMBER:
            value = e.target.valueAsNumber || e.target.value;
            break;
          case INPUT_TYPES.NUMERIC:
            value = data.floatValue;
            break;
          case INPUT_TYPES.TIME:
            value = e.target.value;
            break;
          case INPUT_TYPES.TIME_PICKER:
            value = e;
            break;
          default:
            value = e.target.value;
            break;
        }
        const mutatedValue = applyBeforeOnChange(inputItem, value);
        edit?.update({ [inputItem.name]: mutatedValue });
        applyAfterOnChange(inputItem, mutatedValue);
      };
    }

    inputItem.value = item?.value || edit?.getValue(item.name);
    inputItem.error = edit?.errors?.[item.name];

    return <MiInput item={inputItem} edit={edit} />;
  };

  return (
    <Box className={containerStyle}>
      {headerProps && (
        <MiSectionHeader
          title={headerProps.title}
          subtitle={headerProps.subtitle}
          leftIcon={headerProps.icon}
          leftIconColor={headerProps.leftIconColor}
          onLeftIconClick={headerProps.onLeftIconClick}
          rightIcon={headerProps.rightIcon}
          rightIconColor={headerProps.rightIconColor}
          onRightIconClick={headerProps.onRightIconClick}
          color={headerProps.color}
          rightAction={headerProps.rightAction}
          variant={headerProps.variant}
          style={headerProps.style}
        />
      )}
      <Box
        className={
          typeof customWrapperStyle === "string"
            ? `${classes.baseWrapperStyle}  ${customWrapperStyle}`
            : classes.baseWrapperStyle
        }
        style={typeof customWrapperStyle === "object" ? customWrapperStyle : {}}
      >
        {detailFields.map((item) => (
          <Box
            style={{
              padding: item.padding ?? theme.spacing(1),
              minWidth: item.minWidth ?? 150,
              width: item.width || "33%",
              flexGrow: item.flexGrow ?? 1,
              ...item.style,
            }}
            key={`detail-field-${item.name || item.label}`}
          >
            <Box
              style={{
                backgroundColor:
                  item.backgroundColor || theme.palette.common.white,
                borderRadius: 8,
              }}
            >
              {getInput(item)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()(() => ({
  baseWrapperStyle: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },
}));
